import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { DownloadSimple, FileAudio, FileVideo, WarningCircle } from "@phosphor-icons/react";

const MEDIA_STYLE = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 16,
  height: "100%",
  padding: 32,
  color: "#E5E7EB",
  textAlign: "center",
  boxSizing: "border-box",
};

const PLAYER_STYLE = {
  width: "min(100%, 720px)",
  maxHeight: "80%",
};

const MediaPreview = ({
  kind,
  src,
  fileName = "Media file",
  mimeType = "",
  withCredentials = false,
}) => {
  const [browserCanPlay, setBrowserCanPlay] = useState(true);
  const [mediaError, setMediaError] = useState(false);

  useEffect(() => {
    setMediaError(false);

    if (!mimeType || typeof document === "undefined") {
      setBrowserCanPlay(true);
      return;
    }

    try {
      const probe = document.createElement(kind);
      const support = probe.canPlayType?.(mimeType);
      setBrowserCanPlay(support !== "");
    } catch {
      // Keep the player visible when a browser cannot answer the probe. The
      // media element's error event still provides the final fallback.
      setBrowserCanPlay(true);
    }
  }, [kind, mimeType, src]);

  const showDownload = !browserCanPlay || mediaError;
  const Icon = kind === "audio" ? FileAudio : FileVideo;

  if (showDownload) {
    return (
      <div style={MEDIA_STYLE} role="status">
        <WarningCircle size={42} weight="duotone" color="#FDBA74" aria-hidden="true" />
        <strong>This media file cannot be played in this browser</strong>
        <span>{fileName}</span>
        <a
          href={src}
          download={fileName}
          target="_blank"
          rel="noreferrer"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            color: "#BFDBFE",
            fontWeight: 600,
          }}
        >
          <DownloadSimple size={18} aria-hidden="true" />
          Download or open file
        </a>
      </div>
    );
  }

  const Player = kind === "audio" ? "audio" : "video";
  return (
    <div style={MEDIA_STYLE}>
      <Icon size={42} weight="duotone" color="#BFDBFE" aria-hidden="true" />
      <Player
        controls
        preload="metadata"
        src={src}
        style={PLAYER_STYLE}
        crossOrigin={withCredentials ? "use-credentials" : undefined}
        aria-label={fileName}
        onError={() => setMediaError(true)}
      />
      <span>{fileName}</span>
    </div>
  );
};

MediaPreview.propTypes = {
  kind: PropTypes.oneOf(["audio", "video"]).isRequired,
  src: PropTypes.string.isRequired,
  fileName: PropTypes.string,
  mimeType: PropTypes.string,
  withCredentials: PropTypes.bool,
};

export default MediaPreview;
